import type { ReactElement } from 'react';
import type { TreeNode } from '../api';
import { Link } from '../components/Link';
import { PeopleLine } from './PeopleLine';
import './NodeCard.css';

/**
 * The small paper that floats beside a tapped light (v4.0 §4.17): the
 * situation's title, its one-line definition, how many people stood here, and
 * the door. Nothing of anyone's Ember is on it — a place, not a person.
 *
 * `anchor` is the light's position inside the scene's host element; the card
 * sits to the light's side, flipped when it would leave the host.
 *
 * `tellDoor`: whether the paper offers "I've been here too". The Result's
 * seals turn it off (2026-09-06 走查): the person reading that page just
 * stood here — inviting them to stand here again read as a mistake.
 */
export function NodeCard({ node, anchor, hostWidth, onTell, onClose, placeLink = true, tellDoor = true, logDiscovery = 'tree' }: {
  node: TreeNode;
  anchor: { x: number; y: number };
  hostWidth: number;
  onTell?: (node: TreeNode) => void;
  onClose: () => void;
  placeLink?: boolean;
  tellDoor?: boolean;
  logDiscovery?: string;
}): ReactElement {
  const width = Math.min(264, Math.max(200, hostWidth * 0.62));
  const flip = anchor.x + 26 + width > hostWidth - 12;
  const left = flip ? Math.max(12, anchor.x - 26 - width) : anchor.x + 26;
  const top = Math.max(64, anchor.y - 34);
  return (
    <div
      className={`node-card${flip ? ' is-flipped' : ''}`}
      style={{ left, top, width }}
      role="dialog"
      aria-label={node.title}
      data-log-seen="tree_light_open"
      data-log-node-id={node.id}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button type="button" className="node-card-close" aria-label="Close" onClick={onClose}>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden><path d="m6 6 12 12M18 6 6 18" /></svg>
      </button>
      <p className="node-card-title">{node.title}</p>
      <p className="node-card-def">{node.definition}</p>
      <p className="node-card-people"><i aria-hidden /><span><PeopleLine count={node.people_count} /></span></p>
      {tellDoor || placeLink ? (
        <div className="node-card-actions">
          {tellDoor ? (
            <button
              type="button"
              className="node-card-cta"
              data-log="tree_light_tell"
              data-log-node-id={node.id}
              data-log-discovery={logDiscovery}
              onClick={() => onTell?.(node)}
            >
              I’ve been here too <span aria-hidden>→</span>
            </button>
          ) : null}
          {placeLink ? (
            <Link to={`/places/${encodeURIComponent(node.slug)}`} className={tellDoor ? 'node-card-more' : 'node-card-cta'} data-log="tree_light_open" data-log-node-id={node.id} data-log-state="place">
              What did they go through?{tellDoor ? null : <span aria-hidden>→</span>}
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
